/* The control layer of a flowing conversation. Every target is 52px with an icon and a word —
   nothing here is a 9px text link. */
import { Keyboard, Mic, MicOff, Volume2 } from "lucide-react";

const LANGS = [["auto", "AUTO"], ["en", "EN"], ["sw", "SW"]];

/* ── the language control: a segmented pill flanking the stage on the right edge, so it is reachable
   without crowding either the header or the controls. ── */
export const LanguagePill = ({ lang, onLang, accent, vertical = false }) => (
  <div
    data-testid="flow-lang-pill"
    className={`glass flex gap-0.5 rounded-full p-1 ${vertical ? "flex-col" : "flex-row"}`}
    style={{ borderColor: "var(--border-linen)" }}
  >
    {LANGS.map(([k, label]) => {
      const on = lang === k;
      return (
        <button
          key={k}
          data-testid={`lang-${k}-btn`}
          onClick={() => onLang?.(k)}
          aria-pressed={on}
          className="flex h-9 min-w-[2.6rem] items-center justify-center rounded-full font-mono text-[10px] tracking-[0.14em] transition-all duration-200 active:scale-95"
          style={on
            ? { background: accent, color: "#0C0C0B", boxShadow: `0 0 18px ${accent}66` }
            : { color: "var(--text-faint)" }}
        >
          {label}
        </button>
      );
    })}
  </div>
);

/* ── the controls: a grounded tray of labelled tiles, chosen for legibility and for thumbs ── */
// One way out, and it is the X in the header. The tray used to carry a second "leave" that did
// something subtly different (it dropped you into the old press-and-speak console), which is exactly
// how a visitor ended up on a screen nobody meant to keep.
export const FlowControls = ({ muted, toggleMute, onSound, soundOpen, allowText, toText, accent }) => {
  const Row = ({ testId, onClick, icon, label, tone }) => (
    <button
      data-testid={testId}
      onClick={onClick}
      className="flex h-[52px] flex-1 flex-col items-center justify-center gap-1 rounded-2xl border transition-all duration-200 active:scale-[0.97]"
      style={tone === "live"
        ? { borderColor: `${accent}88`, background: `${accent}16`, color: accent }
        : tone === "leave"
          ? { borderColor: "rgba(166,91,74,0.45)", background: "rgba(166,91,74,0.1)", color: "#C2705C" }
          : { borderColor: "var(--border-linen)", background: "rgba(244,239,230,0.03)", color: "var(--text-dim)" }}
    >
      {icon}
      <span className="font-mono text-[8.5px] uppercase tracking-[0.16em]">{label}</span>
    </button>
  );
  return (
    <div data-testid="flow-bar" className="glass flex w-full max-w-[24rem] gap-2 rounded-3xl p-2" style={{ borderColor: "var(--border-linen)" }}>
      <Row testId="flow-mute-btn" onClick={toggleMute} tone={muted ? "plain" : "live"} label={muted ? "muted" : "live"} icon={muted ? <MicOff size={17} /> : <Mic size={17} />} />
      <Row testId="flow-sound-btn" onClick={onSound} tone={soundOpen ? "live" : "plain"} label="volume" icon={<Volume2 size={17} />} />
      {allowText && <Row testId="flow-text-btn" onClick={toText} tone="plain" label="type" icon={<Keyboard size={17} />} />}
    </div>
  );
};
